import React, { useState, useEffect } from 'react';
import { 
  Magnet, Calculator, Info, Download, Eye, Save, 
  List, Plus, FileText, Thermometer, AlertTriangle,
  Box, Layers, Shield, Zap, Heart, Settings
} from 'lucide-react';
import { 
  calculateMagnetRequirement,
  calculateMagnetPrice,
  generateSafetyWarnings,
  MAGNET_GRADES,
  MAGNET_PULL_FORCE_DATA,
  MagnetRequirement
} from '@/utils/magnetCalculatorAdvanced';
import {
  calculatePrecisionPullForce,
  calculateRequiredMagnets,
  compareGrades,
  recommendOptimalGrade,
  generateTechnicalReport,
  NEO_GRADES,
  type PrecisionMagnetInput
} from '@/utils/magnetCalculatorPrecision';
import { Magnet3DVisualization } from './Magnet3DVisualization';
import { PrecisionMagnetCalculator } from './PrecisionMagnetCalculator';
import { generateAdvancedMagnetPDF } from '@/utils/generateAdvancedMagnetPDF';
import { useOffer } from '@/context/OfferContext';

interface SavedCalculation {
  id: string;
  productName: string;
  result: MagnetRequirement;
  containerDimensions: {
    width: number;
    length: number;
    height: number;
  };
  totalWeight: number;
  temperature: number;
  savedAt: string;
}

export const AdvancedMagnetCalculator: React.FC = () => {
  // Stan formularza
  const [formData, setFormData] = useState({
    containerWidth: 300,
    containerLength: 200,
    containerHeight: 50,
    containerWeight: 0.5,
    contentWeight: 2.0,
    steelThickness: 1.5,
    orientation: 'vertical' as 'vertical' | 'horizontal' | 'angled',
    angle: 0,
    temperature: 20,
    safetyFactor: 3,
    preferredGrade: 'N42',
    maxMagnetSize: 30,
    magnetShape: 'disc' as 'disc' | 'block' | 'ring',
    surfaceCondition: 'clean' as 'clean' | 'painted' | 'rough',
    airGap: 0
  });

  const [result, setResult] = useState<MagnetRequirement | null>(null);
  const [show3D, setShow3D] = useState(false);
  const [showMagneticField, setShowMagneticField] = useState(false);
  const [productName, setProductName] = useState('');
  const [savedCalculations, setSavedCalculations] = useState<SavedCalculation[]>([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'calculator' | 'precision' | 'database' | 'safety'>('calculator');
  const [compareMode, setCompareMode] = useState(false);
  const [comparisonGrades, setComparisonGrades] = useState<string[]>(['N35', 'N42', 'N45', 'N52']);
  
  const { addItem } = useOffer();

  // Wczytaj zapisane kalkulacje
  useEffect(() => {
    const saved = localStorage.getItem('advancedMagnetCalculations');
    if (saved) {
      setSavedCalculations(JSON.parse(saved));
    }
  }, []);

  // Obsługa zmiany danych formularza
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  // Kalkulacja wymagań magnetycznych
  const handleCalculate = () => {
    const totalWeight = formData.containerWeight + formData.contentWeight;
    
    const requirement = calculateMagnetRequirement(
      totalWeight,
      formData.orientation,
      formData.angle,
      formData.containerWidth,
      formData.containerLength,
      formData.steelThickness,
      formData.temperature,
      formData.safetyFactor,
      formData.preferredGrade,
      formData.maxMagnetSize
    );

    // Dodaj korekty dla warunków powierzchni
    if (formData.surfaceCondition === 'painted') {
      requirement.singleMagnetForce *= 0.9;
      requirement.warnings.push('Malowana powierzchnia redukuje siłę przyciągania o ~10%');
    } else if (formData.surfaceCondition === 'rough') {
      requirement.singleMagnetForce *= 0.8;
      requirement.warnings.push('Nierówna powierzchnia redukuje siłę przyciągania o ~20%');
    }

    // Korekta dla szczeliny powietrznej
    if (formData.airGap > 0) {
      const gapFactor = Math.pow(1 / (1 + formData.airGap / 2), 2);
      requirement.singleMagnetForce *= gapFactor;
      requirement.warnings.push(`Szczelina ${formData.airGap}mm redukuje siłę o ${((1 - gapFactor) * 100).toFixed(0)}%`);
    }

    setResult(requirement);
  };

  // Porównanie różnych gatunków
  const handleCompareGrades = () => {
    const comparisons = comparisonGrades.map(grade => {
      const totalWeight = formData.containerWeight + formData.contentWeight;
      return {
        grade,
        requirement: calculateMagnetRequirement(
          totalWeight,
          formData.orientation,
          formData.angle,
          formData.containerWidth,
          formData.containerLength,
          formData.steelThickness,
          formData.temperature,
          formData.safetyFactor,
          grade,
          formData.maxMagnetSize
        )
      };
    });

    // Tutaj można wyświetlić porównanie w modalnym oknie lub tabeli
    console.log('Porównanie gatunków:', comparisons);
  };

  // Zapisz kalkulację
  const handleSaveCalculation = () => {
    if (!result || !productName.trim()) return;

    const newCalculation: SavedCalculation = {
      id: Date.now().toString(),
      productName,
      result,
      containerDimensions: {
        width: formData.containerWidth,
        length: formData.containerLength,
        height: formData.containerHeight
      },
      totalWeight: formData.containerWeight + formData.contentWeight,
      temperature: formData.temperature,
      savedAt: new Date().toISOString()
    };

    const updated = [...savedCalculations, newCalculation];
    setSavedCalculations(updated);
    localStorage.setItem('advancedMagnetCalculations', JSON.stringify(updated));
    
    alert('Kalkulacja została zapisana!');
    setProductName('');
  };

  // Dodaj do oferty
  const handleAddToOffer = () => {
    if (!result || !productName.trim()) return;

    const diameter = result.shape.dimensions.diameter || 20;
    const thickness = result.shape.dimensions.height || 5;
    const unitPrice = calculateMagnetPrice(
      result.grade,
      diameter,
      thickness,
      1
    );

    const magnetItem = {
      id: Date.now(),
      name: `Magnesy ${result.grade} dla ${productName}`,
      description: `Magnesy neodymowe ${result.grade} Ø${diameter}×${thickness}mm`,
      magnetType: result.grade,
      diameter,
      thickness,
      quantity: result.quantity,
      unitPrice,
      totalPrice: calculateMagnetPrice(result.grade, diameter, thickness, result.quantity),
      isMagnet: true,
      containerDimensions: {
        length: formData.containerLength,
        width: formData.containerWidth,
        height: formData.containerHeight
      }
    };

    addItem(magnetItem);
    alert('Magnesy zostały dodane do oferty!');
  };

  // Generuj PDF
  const handleGeneratePDF = () => {
    if (!result || !productName.trim()) return;
    
    generateAdvancedMagnetPDF(
      formData,
      result,
      productName,
      generateSafetyWarnings(
        result.shape.dimensions.diameter || 20,
        result.shape.dimensions.height || 5,
        result.quantity,
        result.grade
      )
    );
  };

  return (
    <div className="space-y-6">
      {/* Zakładki */}
      <div className="flex space-x-4 border-b border-zinc-700">
        <button
          onClick={() => setSelectedTab('calculator')}
          className={`pb-2 px-4 ${selectedTab === 'calculator' 
            ? 'border-b-2 border-orange-500 text-orange-500' 
            : 'text-gray-400 hover:text-white'}`}
        >
          <Calculator className="w-4 h-4 inline mr-2" />
          Kalkulator
        </button>
        <button
          onClick={() => setSelectedTab('precision')}
          className={`pb-2 px-4 ${selectedTab === 'precision' 
            ? 'border-b-2 border-orange-500 text-orange-500' 
            : 'text-gray-400 hover:text-white'}`}
        >
          <Zap className="w-4 h-4 inline mr-2" />
          Kalkulator precyzyjny
        </button>
        <button
          onClick={() => setSelectedTab('database')}
          className={`pb-2 px-4 ${selectedTab === 'database' 
            ? 'border-b-2 border-orange-500 text-orange-500' 
            : 'text-gray-400 hover:text-white'}`}
        >
          <Layers className="w-4 h-4 inline mr-2" />
          Baza magnesów
        </button>
        <button
          onClick={() => setSelectedTab('safety')}
          className={`pb-2 px-4 ${selectedTab === 'safety' 
            ? 'border-b-2 border-orange-500 text-orange-500' 
            : 'text-gray-400 hover:text-white'}`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          BHP i bezpieczeństwo
        </button>
      </div>

      {/* Zawartość zakładek */}
      {selectedTab === 'calculator' && (
        <>
          {/* Przyciski akcji */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setCompareMode(!compareMode)}
              className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all flex items-center gap-2"
            >
              <Layers className="w-4 h-4" />
              Porównaj gatunki
            </button>
            <button
              onClick={() => setShowSavedList(!showSavedList)}
              className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all flex items-center gap-2"
            >
              <List className="w-4 h-4" />
              Zapisane ({savedCalculations.length})
            </button>
          </div>

          {/* Lista zapisanych kalkulacji */}
          {showSavedList && (
            <div className="bg-zinc-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">Zapisane kalkulacje</h3>
              {savedCalculations.length === 0 ? (
                <p className="text-gray-400">Brak zapisanych kalkulacji</p>
              ) : (
                <div className="space-y-3">
                  {savedCalculations.map(calc => (
                    <div key={calc.id} className="bg-zinc-800 rounded-lg p-4 flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-white">{calc.productName}</h4>
                        <p className="text-sm text-gray-400">
                          {calc.result.grade} - {calc.result.quantity} szt. | 
                          Temp: {calc.temperature}°C | 
                          Waga: {calc.totalWeight.toFixed(1)}kg
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(calc.savedAt).toLocaleDateString('pl-PL')}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSavedCalculations(savedCalculations.filter(c => c.id !== calc.id));
                          localStorage.setItem('advancedMagnetCalculations', 
                            JSON.stringify(savedCalculations.filter(c => c.id !== calc.id)));
                        }}
                        className="text-red-400 hover:text-red-300 transition-all"
                      >
                        Usuń
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Formularz główny */}
          <div className="bg-zinc-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-white flex items-center gap-2">
              <Magnet className="w-5 h-5 text-orange-500" />
              Zaawansowany kalkulator magnesów neodymowych
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Wymiary pojemnika */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400 flex items-center gap-1">
                  <Box className="w-4 h-4" />
                  Wymiary pojemnika
                </h4>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Szerokość (mm)</label>
                  <input
                    type="number"
                    name="containerWidth"
                    value={formData.containerWidth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Długość (mm)</label>
                  <input
                    type="number"
                    name="containerLength"
                    value={formData.containerLength}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Wysokość (mm)</label>
                  <input
                    type="number"
                    name="containerHeight"
                    value={formData.containerHeight}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Obciążenia */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400">Obciążenia</h4>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Waga pojemnika (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="containerWeight"
                    value={formData.containerWeight}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Waga zawartości (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="contentWeight"
                    value={formData.contentWeight}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Współczynnik bezpieczeństwa</label>
                  <input
                    type="number"
                    step="0.5"
                    name="safetyFactor"
                    value={formData.safetyFactor}
                    onChange={handleInputChange}
                    min="1.5"
                    max="5"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Warunki montażu */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400">Warunki montażu</h4>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Orientacja</label>
                  <select
                    name="orientation"
                    value={formData.orientation}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="vertical">Pionowa</option>
                    <option value="horizontal">Pozioma</option>
                    <option value="angled">Pod kątem</option>
                  </select>
                </div>
                {formData.orientation === 'angled' && (
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Kąt (°)</label>
                    <input
                      type="number"
                      name="angle"
                      value={formData.angle}
                      onChange={handleInputChange}
                      min="0"
                      max="90"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Grubość blachy (mm)</label>
                  <input
                    type="number"
                    step="0.5"
                    name="steelThickness"
                    value={formData.steelThickness}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Stan powierzchni</label>
                  <select
                    name="surfaceCondition"
                    value={formData.surfaceCondition}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="clean">Czysta stal</option>
                    <option value="painted">Malowana</option>
                    <option value="rough">Nierówna</option>
                  </select>
                </div>
              </div>

              {/* Parametry magnesów */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400 flex items-center gap-1">
                  <Settings className="w-4 h-4" />
                  Parametry magnesów
                </h4>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">
                    <Thermometer className="w-3 h-3 inline" /> Temperatura pracy (°C)
                  </label>
                  <input
                    type="number"
                    name="temperature"
                    value={formData.temperature}
                    onChange={handleInputChange}
                    min="-40"
                    max="200"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Preferowany gatunek</label>
                  <select
                    name="preferredGrade"
                    value={formData.preferredGrade}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="N35">N35 (standard)</option>
                    <option value="N38">N38</option>
                    <option value="N42">N42 (popularny)</option>
                    <option value="N45">N45</option>
                    <option value="N48">N48</option>
                    <option value="N50">N50</option>
                    <option value="N52">N52 (najsilniejszy)</option>
                    <option value="N35M">N35M (do 100°C)</option>
                    <option value="N42M">N42M (do 100°C)</option>
                    <option value="N35H">N35H (do 120°C)</option>
                    <option value="N42H">N42H (do 120°C)</option>
                    <option value="N35SH">N35SH (do 150°C)</option>
                    <option value="N42SH">N42SH (do 150°C)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Max średnica (mm)</label>
                  <input
                    type="number"
                    name="maxMagnetSize"
                    value={formData.maxMagnetSize}
                    onChange={handleInputChange}
                    min="5"
                    max="50"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Szczelina powietrzna (mm)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="airGap"
                    value={formData.airGap}
                    onChange={handleInputChange}
                    min="0"
                    max="5"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Ostrzeżenia */}
            {formData.temperature > 60 && (
              <div className="mt-4 p-3 bg-yellow-500/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-400">
                  <p>Temperatura {formData.temperature}°C przekracza limit dla standardowych magnesów.</p>
                  <p>Rozważ użycie magnesów wysokotemperaturowych (M, H, SH).</p>
                </div>
              </div>
            )}

            {formData.steelThickness < 2 && (
              <div className="mt-4 p-3 bg-red-500/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-400">
                  <p>Grubość blachy {formData.steelThickness}mm znacznie redukuje siłę magnesów!</p>
                  <p>Siła może być zredukowana nawet do 40% wartości nominalnej.</p>
                </div>
              </div>
            )}

            <button
              onClick={handleCalculate}
              className="mt-6 w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
            >
              <Calculator className="w-4 h-4" />
              Oblicz optymalne magnesy
            </button>
          </div>

          {/* Wyniki */}
          {result && (
            <div className="bg-zinc-700 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  Wyniki kalkulacji
                </h3>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Nazwa produktu"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    className="px-3 py-1 bg-zinc-800 border border-zinc-600 rounded text-white text-sm focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={handleSaveCalculation}
                    disabled={!productName.trim()}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    <Save className="w-4 h-4" />
                    Zapisz
                  </button>
                  <button
                    onClick={handleAddToOffer}
                    disabled={!productName.trim()}
                    className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Do oferty
                  </button>
                  <button
                    onClick={() => setShow3D(!show3D)}
                    className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-all flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    {show3D ? 'Ukryj' : 'Pokaż'} 3D
                  </button>
                  <button
                    onClick={handleGeneratePDF}
                    disabled={!productName.trim()}
                    className="px-3 py-1 bg-zinc-600 text-white rounded hover:bg-zinc-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    PDF
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-zinc-800 rounded-lg p-4">
                  <h4 className="text-sm text-gray-400 mb-2">Rekomendowany magnes</h4>
                  <p className="text-2xl font-bold text-orange-500">{result.grade}</p>
                  <p className="text-sm text-gray-300 mt-1">
                    Ø{result.shape.dimensions.diameter || 20}mm × {result.shape.dimensions.height || 5}mm
                  </p>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4">
                  <h4 className="text-sm text-gray-400 mb-2">Ilość magnesów</h4>
                  <p className="text-2xl font-bold text-white">{result.quantity} szt.</p>
                  <p className="text-sm text-gray-300 mt-1">
                    Układ: {result.arrangement.rows}×{result.arrangement.cols}
                  </p>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4">
                  <h4 className="text-sm text-gray-400 mb-2">Całkowita siła</h4>
                  <p className="text-2xl font-bold text-green-400">
                    {result.totalPullForce.toFixed(0)} N
                  </p>
                  <p className="text-sm text-gray-300 mt-1">
                    {(result.totalPullForce / 9.81).toFixed(1)} kg
                  </p>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4">
                  <h4 className="text-sm text-gray-400 mb-2">Szacowany koszt</h4>
                  <p className="text-2xl font-bold text-blue-400">
                    {calculateMagnetPrice(
                      result.grade,
                      result.shape.dimensions.diameter || 20,
                      result.shape.dimensions.height || 5,
                      result.quantity
                    ).toFixed(2)} PLN
                  </p>
                  <p className="text-sm text-gray-300 mt-1">
                    {(calculateMagnetPrice(
                      result.grade,
                      result.shape.dimensions.diameter || 20,
                      result.shape.dimensions.height || 5,
                      result.quantity
                    ) / result.quantity).toFixed(2)} PLN/szt
                  </p>
                </div>
              </div>

              {/* Ostrzeżenia */}
              {result.warnings.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-500/20 rounded-lg">
                  <h4 className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Ostrzeżenia
                  </h4>
                  <ul className="list-disc list-inside text-sm text-yellow-300 space-y-1">
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Rekomendacje */}
              {result.recommendations.length > 0 && (
                <div className="mt-4 p-4 bg-blue-500/20 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Rekomendacje
                  </h4>
                  <ul className="list-disc list-inside text-sm text-blue-300 space-y-1">
                    {result.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Wizualizacja 3D */}
              {show3D && (
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-white mb-3">Wizualizacja 3D rozmieszczenia magnesów</h4>
                  <div className="h-[500px] bg-zinc-900 rounded-lg overflow-hidden">
                    <Magnet3DVisualization
                      containerWidth={formData.containerWidth}
                      containerHeight={formData.containerHeight}
                      containerDepth={formData.containerLength}
                      magnetDiameter={result.shape.dimensions.diameter || 20}
                      magnetThickness={result.shape.dimensions.height || 5}
                      arrangement={result.arrangement}
                      showMagneticField={showMagneticField}
                      rotationEnabled={true}
                    />
                  </div>
                  <div className="mt-2 flex items-center gap-4">
                    <button
                      onClick={() => setShowMagneticField(!showMagneticField)}
                      className="text-sm text-gray-400 hover:text-white transition-all"
                    >
                      {showMagneticField ? '✓' : '○'} Pokaż pole magnetyczne
                    </button>
                    <span className="text-xs text-gray-500">
                      Użyj myszy do obracania widoku
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Zakładka: Baza magnesów */}
      {selectedTab === 'database' && (
        <div className="bg-zinc-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-white">Baza danych magnesów neodymowych</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-600">
                  <th className="text-left py-2 px-3 text-gray-400">Gatunek</th>
                  <th className="text-left py-2 px-3 text-gray-400">Br (mT)</th>
                  <th className="text-left py-2 px-3 text-gray-400">Hcj (kA/m)</th>
                  <th className="text-left py-2 px-3 text-gray-400">BHmax (kJ/m³)</th>
                  <th className="text-left py-2 px-3 text-gray-400">Max temp (°C)</th>
                  <th className="text-left py-2 px-3 text-gray-400">Temp coeff (%/°C)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(MAGNET_GRADES).map(([key, grade]) => (
                  <tr key={key} className="border-b border-zinc-700 hover:bg-zinc-600">
                    <td className="py-2 px-3 text-white font-medium">{grade.name}</td>
                    <td className="py-2 px-3 text-gray-300">{grade.brMax}</td>
                    <td className="py-2 px-3 text-gray-300">{grade.hcjMin}</td>
                    <td className="py-2 px-3 text-gray-300">{grade.bhMax}</td>
                    <td className="py-2 px-3 text-gray-300">{grade.maxTemp}</td>
                    <td className="py-2 px-3 text-gray-300">{grade.tempCoeffBr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Zakładka: Kalkulator precyzyjny */}
      {selectedTab === 'precision' && (
        <PrecisionMagnetCalculator />
      )}

      {/* Zakładka: BHP */}
      {selectedTab === 'safety' && (
        <div className="bg-zinc-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            Bezpieczeństwo pracy z magnesami neodymowymi
          </h3>
          
          <div className="space-y-6">
            <div className="p-4 bg-red-500/20 rounded-lg">
              <h4 className="font-medium text-red-400 mb-2 flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Rozruszniki serca
              </h4>
              <p className="text-sm text-gray-300">
                Silne magnesy neodymowe mogą zakłócać pracę rozruszników serca i innych implantów medycznych.
                Zachowaj bezpieczną odległość minimum 30 cm od takich urządzeń.
              </p>
            </div>

            <div className="p-4 bg-yellow-500/20 rounded-lg">
              <h4 className="font-medium text-yellow-400 mb-2">Siła przyciągania</h4>
              <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                <li>Magnesy mogą przyciągnąć się z dużą siłą - ryzyko przytrzaśnięcia palców</li>
                <li>Używaj rękawic ochronnych podczas montażu</li>
                <li>Montuj magnesy pojedynczo, zachowując bezpieczną odległość</li>
                <li>Większe magnesy wymagają 2 osób do bezpiecznego montażu</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-500/20 rounded-lg">
              <h4 className="font-medium text-blue-400 mb-2">Kruche materiały</h4>
              <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                <li>Magnesy neodymowe są kruche i mogą pękać przy uderzeniu</li>
                <li>Odpryski mogą być ostre - używaj okularów ochronnych</li>
                <li>Nie upuszczaj magnesów - mogą się rozbić</li>
                <li>Przechowuj w bezpiecznym opakowaniu</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-500/20 rounded-lg">
              <h4 className="font-medium text-purple-400 mb-2">Urządzenia elektroniczne</h4>
              <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                <li>Trzymaj z dala od kart kredytowych i kart magnetycznych</li>
                <li>Mogą uszkodzić dyski twarde i pamięci flash</li>
                <li>Zachowaj odległość od monitorów CRT i telewizorów</li>
                <li>Mogą wpływać na działanie zegarków mechanicznych</li>
              </ul>
            </div>

            <div className="p-4 bg-green-500/20 rounded-lg">
              <h4 className="font-medium text-green-400 mb-2">Temperatura</h4>
              <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                <li>Standardowe magnesy N tracą siłę powyżej 80°C</li>
                <li>Spadek siły: -0.11% na każdy stopień Celsjusza</li>
                <li>Dla wyższych temperatur używaj gatunków SH, UH, EH</li>
                <li>Nagłe zmiany temperatury mogą spowodować pękanie</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
